import React from "react"
import cx from "classnames"
import { twMerge } from "tailwind-merge"
import { Icon } from "."
interface RenderProps {
	row: any
	key: string
}

export interface TableColumnProps {
	label: string
	key: string
	keyForSorting?: string
}
export interface TableColumnExtendedProps extends TableColumnProps {
	render?: (data: RenderProps) => React.ReactNode
	headerRender?: (data: TableColumnProps) => React.ReactNode
	className?: string
}
export interface TableProps {
	columns: TableColumnExtendedProps[]
	rows: any[] | any
	onRowClick?: (id: string) => void
	selectRow?: boolean
	hideHeader?: boolean
	selectedRowColor?: string
	selectedRows?: string[]
	rowClassName?: string
	headerClassName?: string
	headerRowClassName?: string
	tableClassName?: string
	checkInvalidRow?(row: any): boolean | undefined
	invalidRowColor?: string
	sortingEnabled?: boolean
	multiColumnSortingEnabled?: boolean
	loading?: boolean
}

const Table: React.FC<TableProps> = ({
	columns,
	rows,
	onRowClick = () => undefined,
	hideHeader = false,
	selectRow = false,
	selectedRowColor = "#c4e5f8",
	selectedRows,
	rowClassName = "",
	headerClassName = "",
	headerRowClassName = "",
	tableClassName = "",
	checkInvalidRow = () => false,
	invalidRowColor = "#FF2400",
	loading = false,
}) => {
	return (
		<table className={twMerge("whitespace-no-wrap w-full table-auto text-left", tableClassName)}>
			{!hideHeader && (
				<thead className="sticky top-0 z-10 text-sm">
					<tr className={twMerge("bg-gray-3 h-9 text-left", headerRowClassName)}>
						{columns.map(({ label, key, keyForSorting = "", headerRender }, colIndex) => {
							const thClassName = twMerge(
								cx({
									"bg-background px-2.5 py-1.25 text-sm text-left font-medium text-dark-alt pl-4":
										true,
									"rounded-tl-md pl-4": colIndex === 0,
									"rounded-tr-2.5": colIndex === columns.length - 1,
								}),
								headerClassName,
								columns[colIndex].className,
							)

							return (
								<th key={colIndex} className={twMerge(thClassName, headerClassName)}>
									{headerRender && key ? headerRender({ label, key, keyForSorting }) : label}
								</th>
							)
						})}
					</tr>
				</thead>
			)}
			<tbody>
				{rows?.length ? (
					rows.map((row: any, rowIndex: any) => {
						return (
							<React.Fragment key={rowIndex}>
								{/* MAIN ROW */}
								<tr
									onClick={() => onRowClick(row._id)}
									className={twMerge(
										"border-gray-4 h-11 border-t-0.125 border-b border-solid text-sm ",
										rowClassName,
									)}
									style={{
										...(selectRow && !checkInvalidRow(row) && selectedRows?.includes(row._id)
											? { backgroundColor: selectedRowColor }
											: checkInvalidRow(row)
											? { backgroundColor: invalidRowColor }
											: {}),
									}}
								>
									{columns.map(({ key, render, className }, colIndex) => (
										<td
											key={colIndex}
											className={twMerge("px-2.5 py-1.25 pl-4 text-left", className)}
										>
											{render ? render({ row, key }) : row[key]}
										</td>
									))}
								</tr>

								{/* ADDITIONAL ROW (FIXED BORDER) */}
								{row?.renderAdditionalRow && (
									<tr className="border-b-0.125 border-l-0.125 border-r-0.125 border-[#667085]">
										<td colSpan={columns.length} className="border-0 p-0">
											{row.renderAdditionalRow({ row })}
										</td>
									</tr>
								)}
							</React.Fragment>
						)
					})
				) : (
					<tr>
						<th colSpan={12} className="p-3 text-center text-dark-alt">
							No Data
						</th>
					</tr>
				)}

				{/* LOADING ROW */}
				{loading && (
					<tr>
						<th colSpan={12} className="p-3 text-left text-dark-alt">
							<div className="flex">
								<Icon name="spinner" color="black" size={24} className="animate-spin" />
								<span className="ml-2 text-sm text-gray-600">Loading more employees...</span>
							</div>
						</th>
					</tr>
				)}
			</tbody>
		</table>
	)
}

export default Table
